import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState
} from 'recoil'

import {
  Flex,
  FlexItem,
  Loading,
  SPACING_24,
  SpacingVertical,
  TOAST_VARIANT_INFO,
  TOAST_VARIANT_SUCCESS,
  TOAST_VARIANT_WARNING,
  ToastContainer,
  toastShow,
  Tooltip
} from 'mailup-web-components'

import { notifyParent, STORE_KEY_ID_LIST, storeGet } from 'mailup-react-helpers'

import { pendoTrack } from 'Helpers/pendoTracking'

import BoxThumbnail from 'Pages/OverviewMessage/BoxThumbnail'
import CheckUpBox from './components/CheckUpBox'
import Footer from './components/Footer'
import Header from './components/Header'
import InlineMessageBox from './components/InlineMessageBox'
import ModalConfirmLeave from './components/ModalConfirmLeave'

import ErrorFeedback from 'Components/ErrorFeedback'

import { cleanupCallback, initCallback } from './recoil/callbacks/callbacks'
import { canEditState, messageState } from './recoil/state/message'
import { pageState } from './recoil/state/page'

import styled from '@emotion/styled'
import ModalBuyCollaboration from 'Components/ModalBuyCollaboration'
import {
  MESSAGE_STR_TYPE_CONFIRMATION,
  MESSAGE_STR_TYPE_WELCOME
} from 'Constants/index'
import { isOfferLight } from 'Helpers/options'
import BoxAttachments from 'Pages/OverviewMessage/BoxAttachments'
import BoxCollaboration from 'Pages/OverviewMessage/BoxCollaboration'
import BoxFooter from 'Pages/OverviewMessage/BoxFooter'
import BoxNotification from 'Pages/OverviewMessage/BoxNotification'
import ModalConfirmCopy from 'Pages/OverviewMessage/ModalConfirmCopy'
import ModalConfirmCopyHtml from 'Pages/OverviewMessage/ModalConfirmCopyHtml'
import ModalNoEdit from 'Pages/OverviewMessage/ModalNoEdit'
import ModalNotificationMessage from 'Pages/OverviewMessage/ModalNotificationMessage'
import {
  modalBuyCollaboration,
  modalConfirmChangeDefaultMessageState
} from 'Pages/OverviewMessage/recoil/state/modal'
import BoxAdvancedSettings from './BoxAdvancedSettings/BoxAdvancedSettings'
import BoxInput from './BoxInput/BoxInput'
import ModalAlertMessageUpdate from './components/ModalAlertMessageUpdate'
import ModalConfirmChangeDefault from './components/ModalConfirmChangeDefault'
import ModalCheckup from './ModalCheckup'

const OverviewMessage = () => {
  const { id } = useParams()
  const intl = useIntl()
  const history = useHistory()

  const qsParams = new URLSearchParams(useLocation().search)
  const op = qsParams.get('op')
  const content = qsParams.get('content')

  const {
    loading,
    loaded,
    imageCheckFail,
    error,
    severity,
    newFromLanding,
    newFromLandingWithForm,
    title,
    downloadFile
  } = useRecoilValue(pageState)
  const { isDirty, messageType, isBee, isSending } = useRecoilValue(
    messageState
  )
  const canEdit = useRecoilValue(canEditState)
  const [{ showInfo }, setShowInfo] = useRecoilState(
    modalConfirmChangeDefaultMessageState
  )

  const showModalBuy = useRecoilValue(modalBuyCollaboration)
  const handleCloseModalBuy = useResetRecoilState(modalBuyCollaboration)

  const init = useRecoilCallback(initCallback, [])
  const cleanup = useRecoilCallback(cleanupCallback, [])

  const checkupLink = useCallback(() => {
    const route = `/messages/email/${id}/checkup/linkcheck`
    history.push(route)
  }, [id])

  const editLink = useCallback(() => {
    if (isBee) {
      history.push(`/messages/email/bee/edit/${id}`)
    } else {
      notifyParent('redirectMainFrame', [
        `form_upd_newsletter.aspx?source=listemail&idNL=${id}`
      ])
    }
  }, [id, isBee])

  const reloadPage = useCallback(() => {
    window.location.reload()
  }, [])

  const handleBeforeUnload = e => {
    notifyParent('showLoadingMainFrame', [false])

    if (isDirty) {
      e.preventDefault()

      return (e.returnValue = '')
    } else {
      delete e.returnValue
    }
  }

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload, {
      capture: true
    })

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload, {
        capture: true
      })
    }
  }, [isDirty])

  useEffect(() => {
    init(id, { op, content })

    return () => {
      cleanup()
    }
  }, [])

  useEffect(() => {
    if (showInfo) {
      toastShow(
        <>
          {intl.formatMessage(
            {
              id: 'Load.ModalNotificationAsDefaultWelcome.ToastText'
            },
            {
              link: chunks => <a onClick={handleRedirect}>{chunks}</a>
            }
          )}
        </>,
        TOAST_VARIANT_INFO,
        'setActiveWelcomeMessageCallbackSuccess',
        false,
        true
      )

      setShowInfo(prev => ({ ...prev, showInfo: false, show: false }))
    }
  }, [showInfo])

  const handleRedirect = () => {
    notifyParent('redirectMainFrame', [
      `automation_app_redirector.aspx?r=${encodeURIComponent(
        `/automation/workflow/0/edit?ws=1&idW=${id}`
      )}`
    ])
  }

  useEffect(() => {
    if (imageCheckFail) {
      toastShow(
        <>
          {intl.formatMessage(
            {
              id: 'Load.SavedEmailWizard.UnreachableEmbeddedImagesIntl'
            },
            {
              checkup: chunks => (
                <a key={1} onClick={checkupLink}>
                  {chunks}
                </a>
              )
            }
          )}
        </>,
        TOAST_VARIANT_WARNING,
        'messageIsOnSent'
      )
    }
  }, [imageCheckFail])

  useEffect(() => {
    if (newFromLanding && title) {
      toastShow(
        intl.formatMessage(
          { id: 'Load.SavedEmailWizard.GenerateEmailFromLandingSucces' },
          { 0: title }
        ),
        TOAST_VARIANT_SUCCESS,
        'newFromLanding'
      )

      const idList = storeGet(STORE_KEY_ID_LIST)

      pendoTrack('Email from Landing Page (warning) - Impression', {
        idList,
        idMessage: id,
        source: 'emailMessageOverview'
      })
    }
  }, [newFromLanding, title])

  useEffect(() => {
    if (newFromLandingWithForm) {
      toastShow(
        <>
          {intl.formatMessage(
            { id: 'Load.SavedEmailWizard.GeneratedEmailWithFormIntl' },
            {
              link: chunks => (
                <a
                  data-mu-test='messageOverview-warningEmailFromLanding-click'
                  onClick={editLink}
                >
                  {chunks}
                </a>
              )
            }
          )}
        </>,
        TOAST_VARIANT_WARNING,
        'newFromLandingWithForm'
      )
    }
  }, [newFromLandingWithForm, editLink])

  return (
    <>
      <Header />
      <Loading
        open={loading || downloadFile}
        text={
          downloadFile
            ? intl.formatMessage({
                id: 'Load.EmailMessageOverview.DownloadLoading'
              })
            : ''
        }
      />
      <ToastContainer />
      <Tooltip />
      <ModalCheckup />
      <ModalConfirmLeave />
      <ModalNoEdit />
      <ModalConfirmCopy />
      <ModalConfirmCopyHtml />
      <ModalNotificationMessage />
      <ModalAlertMessageUpdate />
      <ModalBuyCollaboration
        show={showModalBuy}
        onClose={handleCloseModalBuy}
      />
      <ModalConfirmChangeDefault />

      <div className='l-container'>
        {isSending && (
          <InlineMessageBox
            inlineMessage={intl.formatMessage({
              id: 'Load.EmailMessageList.MessageIsOnSentWarning'
            })}
            inlineMessageSeverity='warning'
          />
        )}

        <ErrorFeedback
          error={error}
          severity={severity}
          onClickReload={reloadPage}
        />
        {error && <SpacingVertical variant={SPACING_24} />}
        {!error && loaded && (
          <>
            <Flex>
              <FlexItem flex='0 0 calc(25% - 16px)' margin='0 16px 0 0'>
                <Flex rowGap='24px' flexDirection='column'>
                  <BoxThumbnail />
                  <CheckUpBox />
                </Flex>
              </FlexItem>
              <StyledFlexItem
                flex='0 0 calc(75% - 16px)'
                margin='0 0 0 16px'
                disabled={!canEdit}
              >
                <Flex flexDirection='column'>
                  <BoxInput />
                  {(messageType === MESSAGE_STR_TYPE_WELCOME ||
                    messageType === MESSAGE_STR_TYPE_CONFIRMATION) && (
                    <BoxNotification />
                  )}
                  <BoxFooter />
                  {!isOfferLight() && <BoxCollaboration />}
                  <BoxAttachments />
                  <BoxAdvancedSettings />
                </Flex>
              </StyledFlexItem>
            </Flex>
            <Footer />
          </>
        )}
      </div>
    </>
  )
}

export default OverviewMessage

const StyledFlexItem = styled(FlexItem)`
  .e-collapsible__inner {
    margin-left: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  ${({ disabled }) =>
    disabled && 'opacity: 0.6; cursor: default; pointer-events: none;'}
`
